import { ArtifactIdentifier, ArtifactMap, SendGetRequest } from './types';

type ArtifactServiceType = 'default' | 'candidate' | 'project' | 'risk' | 'staffingNote' | 'budget';

type ArtifactInfoCollector = (ids: string[]) => Promise<ArtifactMap>;

export type ArtifactServiceConfigParams = {
  isCandidateService?: boolean;
  isProjectService?: boolean;
  isRiskService?: boolean;
  isStaffingService?: boolean;
  routeRiskId?: string;
  budgetId?: StringOrNumber;
  sendGetKernelRequest: SendGetRequest;
  sendGetRecruitmentRequest: SendGetRequest;
  sendGetDeliveryRequest: SendGetRequest;
  collectArtifactsData: ArtifactInfoCollector;
  collectCandidateArtifactsData: ArtifactInfoCollector;
  collectProjectArtifactsData: ArtifactInfoCollector;
  collectRiskArtifactsData: ArtifactInfoCollector;
  collectStaffingArtifactsData: ArtifactInfoCollector;
  collectBudgetArtifactsData: ArtifactInfoCollector;
  isBudgetArtifactsInfoReceiverLoading: boolean;
  isProjectArtifactsInfoReceiverLoading: boolean;
  isRiskArtifactsInfoReceiverLoading: boolean;
  isStaffingArtifactsInfoReceiverLoading: boolean;
};

export type ArtifactServiceConfig = {
  sendGetRequest: SendGetRequest;
  collectArtifactsDataAction: ArtifactInfoCollector;
  isArtifactsInfoReceiverLoading: boolean;
  buildArtifactUrl: (id: ArtifactIdentifier, suffix: string) => string;
  uploadUrl: string;
};

const getArtifactServiceType = ({
  isCandidateService,
  isProjectService,
  isRiskService,
  isStaffingService,
  routeRiskId,
  budgetId,
}: Pick<
  ArtifactServiceConfigParams,
  'isCandidateService' | 'isProjectService' | 'isRiskService' | 'isStaffingService' | 'routeRiskId' | 'budgetId'
>): ArtifactServiceType => {
  if (budgetId) {
    return 'budget';
  }

  if (isStaffingService) {
    return 'staffingNote';
  }

  if (isRiskService && routeRiskId) {
    return 'risk';
  }

  if (isProjectService) {
    return 'project';
  }

  if (isCandidateService) {
    return 'candidate';
  }

  return 'default';
};

const getBaseArtifactUrl = ({
  serviceType,
  routeRiskId,
  budgetId,
}: {
  serviceType: ArtifactServiceType;
  routeRiskId?: string;
  budgetId?: StringOrNumber;
}) => {
  switch (serviceType) {
    case 'budget':
      return `/budgets/${budgetId}/documents`;
    case 'risk':
      return `/projects/risks/${routeRiskId}/artifacts`;
    case 'project':
      return '/projects/timeline/artifacts';
    case 'staffingNote':
      return '/projects/staffing-notes/artifacts';
    case 'candidate':
      return '/candidates/artifacts';
    default:
      return '/artifacts';
  }
};

export const buildArtifactServiceConfig = (params: ArtifactServiceConfigParams): ArtifactServiceConfig => {
  const serviceType = getArtifactServiceType(params);
  const baseArtifactUrl = getBaseArtifactUrl({
    serviceType,
    routeRiskId: params.routeRiskId,
    budgetId: params.budgetId,
  });

  const serviceConfigMap: Record<
    ArtifactServiceType,
    Pick<ArtifactServiceConfig, 'sendGetRequest' | 'collectArtifactsDataAction' | 'isArtifactsInfoReceiverLoading'>
  > = {
    project: {
      sendGetRequest: params.sendGetDeliveryRequest,
      collectArtifactsDataAction: params.collectProjectArtifactsData,
      isArtifactsInfoReceiverLoading: params.isProjectArtifactsInfoReceiverLoading,
    },
    risk: {
      sendGetRequest: params.sendGetDeliveryRequest,
      collectArtifactsDataAction: params.collectRiskArtifactsData,
      isArtifactsInfoReceiverLoading: params.isRiskArtifactsInfoReceiverLoading,
    },
    staffingNote: {
      sendGetRequest: params.sendGetDeliveryRequest,
      collectArtifactsDataAction: params.collectStaffingArtifactsData,
      isArtifactsInfoReceiverLoading: params.isStaffingArtifactsInfoReceiverLoading,
    },
    budget: {
      sendGetRequest: params.sendGetDeliveryRequest,
      collectArtifactsDataAction: params.collectBudgetArtifactsData,
      isArtifactsInfoReceiverLoading: params.isBudgetArtifactsInfoReceiverLoading,
    },
    candidate: {
      sendGetRequest: params.sendGetRecruitmentRequest,
      collectArtifactsDataAction: params.collectCandidateArtifactsData,
      isArtifactsInfoReceiverLoading: false,
    },
    default: {
      sendGetRequest: params.sendGetKernelRequest,
      collectArtifactsDataAction: params.collectArtifactsData,
      isArtifactsInfoReceiverLoading: false,
    },
  };

  return {
    ...serviceConfigMap[serviceType],
    buildArtifactUrl: (id, suffix) =>
      `${baseArtifactUrl}/${serviceType === 'staffingNote' ? encodeURIComponent(String(id)) : id}/${suffix}`,
    uploadUrl: serviceType === 'staffingNote' ? '/projects/staffing-notes/artifacts' : '/artifacts',
  };
};
